import { useCallback, MouseEvent as ReactMouseEvent } from 'react'
import type { Node } from '@xyflow/react'
import { useReactFlow } from '@xyflow/react'
import { findGroupAtPoint } from '@/lib/subflow-utils'

export interface UseNodeDragDropOptions {
  nodes: Node[]
  moveNodeToGroup: (nodeId: string, targetGroupId: string | null) => void
  onAttachedToGroup?: (groupId: string) => void
  onActiveGroupChange?: (groupId: string | null) => void
}

export interface UseNodeDragDropReturn {
  onNodeDrag: (event: ReactMouseEvent, draggedNode: Node, draggedNodes: Node[]) => void
  onNodeDragStop: (event: ReactMouseEvent, draggedNode: Node, draggedNodes: Node[]) => void
}

/**
 * Hook personalizado para manejar drag & drop de nodos sobre grupos
 *
 * Detecta cuando un nodo es soltado sobre un grupo y automáticamente
 * mueve el nodo al grupo como hijo.
 */
export function useNodeDragDrop({
  nodes,
  moveNodeToGroup,
  onAttachedToGroup,
  onActiveGroupChange,
}: UseNodeDragDropOptions): UseNodeDragDropReturn {
  const { screenToFlowPosition } = useReactFlow()

  /**
   * Handler para mientras se arrastra un nodo (para mostrar el borde rojo)
   */
  const onNodeDrag = useCallback(
    (event: ReactMouseEvent, draggedNode: Node, draggedNodes: Node[]) => {
      // Solo detectar grupo si arrastramos un solo nodo y no es sticky
      if (draggedNodes.length > 1 || draggedNode.type === 'sticky') {
        onActiveGroupChange?.(null)
        return
      }

      // Obtener la posición del mouse en coordenadas del flow
      const mousePosition = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      })

      // Buscar si hay un grupo en la posición del mouse
      const targetGroup = findGroupAtPoint(mousePosition, nodes, draggedNode.id)

      // Notificar el grupo activo (o null si no hay)
      onActiveGroupChange?.(targetGroup?.id ?? null)
    },
    [nodes, onActiveGroupChange, screenToFlowPosition]
  )

  /**
   * Handler para cuando se suelta un nodo después de arrastrarlo
   */
  const onNodeDragStop = useCallback(
    (event: ReactMouseEvent, draggedNode: Node, draggedNodes: Node[]) => {
      // Si se arrastraron múltiples nodos, no hacer nada
      // (podríamos agregar soporte para esto en el futuro)
      if (draggedNodes.length > 1) {
        return
      }

      // REGLA: Los StickyNote no pueden entrar en SubFlows
      // Solo los nodos (trigger, action) pueden ser hijos de subflows
      if (draggedNode.type === 'sticky') {
        // Si el sticky note está intentando salir de un grupo, permitirlo
        if (draggedNode.parentId) {
          moveNodeToGroup(draggedNode.id, null)
        }
        // No permitir que entren en grupos
        return
      }

      // Obtener la posición del mouse en coordenadas del flow
      const mousePosition = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      })

      // Buscar si hay un grupo en la posición del mouse
      const targetGroup = findGroupAtPoint(mousePosition, nodes, draggedNode.id)

      // Si el nodo ya está en el grupo target, no hacer nada
      if (targetGroup && draggedNode.parentId === targetGroup.id) {
        return
      }

      // Si encontramos un grupo y el nodo no está ya en él, moverlo
      if (targetGroup) {
        moveNodeToGroup(draggedNode.id, targetGroup.id)
        try {
          onAttachedToGroup?.(targetGroup.id)
        } catch (e) {
          // no-op
        }
      } else if (draggedNode.parentId) {
        // Si el nodo tenía un padre pero ya no está sobre ningún grupo,
        // liberarlo (esto permite sacar nodos de grupos)
        moveNodeToGroup(draggedNode.id, null)
      }
    },
    [nodes, moveNodeToGroup, screenToFlowPosition]
  )

  return {
    onNodeDrag,
    onNodeDragStop,
  }
}
